import { useCallback, useEffect, useState } from 'react'
import {
  getMe,
  getMyTasks,
  InvalidLinkError,
  setMyNote,
  setMyStatus,
  type Me,
  type Status,
  type TaskRow,
} from './lib/api'
import { bucketTasks } from './lib/grouping'
import { captureToken, forgetToken, storeToken } from './lib/token'
import TaskCard from './components/TaskCard'
import SignIn from './components/SignIn'
import AdminView from './components/AdminView'
import Scorecard from './components/Scorecard'
import Issues, { type IssuePrefill } from './components/Issues'
import Headlines from './components/Headlines'
import MyPortal from './components/MyPortal'
import Meeting from './components/Meeting'
import Icon, { type IconName } from './components/icons'
import { HeaderSlotProvider } from './components/HeaderAction'
import type { TaskPrefill } from './components/AdminTasks'
import type { Issue } from './lib/issuesApi'
import type { Headline } from './lib/headlinesApi'
import { getCurrentMeeting } from './lib/meetingApi'
import {
  issuePrefillFromTask,
  taskPrefillFromHeadline,
  taskPrefillFromIssue,
  type TaskRef,
} from './lib/meeting'

type Phase = 'loading' | 'ready' | 'no-link' | 'bad-link' | 'error'
type View = 'portal' | 'tasks' | 'headlines' | 'issues' | 'scorecard' | 'meeting' | 'people'

type LaunchIntent = 'new-issue' | 'issues' | null

/**
 * What the URL asked us to open.
 *
 * The shop QR code points here, so scanning it lands on the report form rather
 * than on the task board. That is what let the Google Form be retired: the QR
 * still works, it just goes somewhere that knows who you are.
 *
 * TWO forms are accepted, and the PATH one is what a printed QR should use:
 *
 *   /issue   /issues     ← print this. No '#', so nothing can mangle it, and it
 *                          is short enough to read off a poster and type.
 *   #new-issue #issues   ← the original fragment form, still honoured.
 *
 * The path form needs no redirect rule: `_redirects` already serves index.html
 * for any path, so the SPA boots and reads the path itself.
 *
 * Read BEFORE captureToken(). A personal link carries its credential in the
 * fragment, and captureToken() strips the whole hash once it has one.
 *
 * The marker is removed afterwards so that saving the app to a home screen from
 * a scanned link does not reopen the report form on every single launch — but
 * only the part that carried the intent, so a `/issue#t=…` link keeps its token.
 */
function readLaunchIntent(): LaunchIntent {
  const hash = window.location.hash.replace(/^#/, '')
  const path = window.location.pathname.replace(/\/+$/, '').toLowerCase()

  const hashIntent: LaunchIntent =
    hash === 'new-issue' ? 'new-issue' : hash === 'issues' ? 'issues' : null
  const pathIntent: LaunchIntent =
    path === '/issue' ? 'new-issue' : path === '/issues' ? 'issues' : null

  const intent = hashIntent ?? pathIntent
  if (!intent) return null

  // Keep the fragment untouched unless it was the thing carrying the intent —
  // otherwise a personal link arriving as /issue#t=… would lose its token here,
  // before captureToken ever ran.
  window.history.replaceState(
    {},
    '',
    '/' + window.location.search + (hashIntent ? '' : window.location.hash),
  )
  return intent
}

export default function App() {
  // Declared first, deliberately — see readLaunchIntent.
  const [intent] = useState<LaunchIntent>(readLaunchIntent)
  // Seeded from a personal link if one was opened; otherwise the name picker
  // fills it in. Both routes end up with the same kind of token.
  const [token, setToken] = useState<string | null>(() => captureToken())
  const [phase, setPhase] = useState<Phase>('loading')
  const [me, setMe] = useState<Me | null>(null)
  const [tasks, setTasks] = useState<TaskRow[]>([])
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState<Set<string>>(new Set())
  const [refreshing, setRefreshing] = useState(false)
  // My Portal is the landing view: everything tied to you, gathered in one
  // place, is the point of the tab — it should be what you see first, not one
  // more menu to remember to check. A scanned QR code still overrides this,
  // same as before.
  const [view, setView] = useState<View>(intent ? 'issues' : 'portal')
  // The sidebar is a fixed off-canvas drawer below the desktop breakpoint —
  // this is only ever true on a phone/tablet, where the hamburger that sets
  // it exists. On desktop the sidebar is always visible and nothing here
  // renders a hamburger, so this stays false.
  const [navOpen, setNavOpen] = useState(false)
  // The empty element in the page header that each view portals its primary
  // action ("+ New task", …) into — see HeaderAction.tsx. State rather than a
  // ref so the views re-render once it exists.
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null)
  const [openIssueForm, setOpenIssueForm] = useState(intent === 'new-issue')
  // Set by Issues → "Create a task", consumed by AdminTasks in the Tasks view.
  const [taskPrefill, setTaskPrefill] = useState<TaskPrefill | null>(null)
  // Set by Headlines → "Create an issue", consumed by Issues. The EOS chain runs
  // headline → issue → task, and each hop hands off the same way.
  const [issuePrefill, setIssuePrefill] = useState<IssuePrefill | null>(null)
  // Whether a Shop L10 is running right now — a LIVE chip on the sidebar
  // item. Checked once per load; the Meeting view itself polls continuously.
  const [meetingLive, setMeetingLive] = useState(false)

  const load = useCallback(
    async (initial: boolean) => {
      if (!token) {
        setPhase('no-link')
        return
      }
      if (!initial) setRefreshing(true)
      try {
        const [who, rows, live] = await Promise.all([
          getMe(token),
          getMyTasks(token),
          // Never let the chip break the load: no meeting on failure.
          getCurrentMeeting(token).then((m) => m !== null).catch(() => false),
        ])
        setMe(who)
        setTasks(rows)
        setMeetingLive(live)
        setPhase('ready')
        setProblem(null)
      } catch (err) {
        if (err instanceof InvalidLinkError) {
          setPhase('bad-link')
        } else if (initial) {
          setPhase('error')
          setProblem(err instanceof Error ? err.message : String(err))
        } else {
          // Already showing good data — surface the failure without blanking it.
          setProblem(err instanceof Error ? err.message : String(err))
        }
      } finally {
        setRefreshing(false)
      }
    },
    [token],
  )

  useEffect(() => {
    void load(true)
  }, [load])

  // Escape closes the drawer, and the page behind it must not scroll while
  // it's open — same pattern as the full-screen PDF viewer in PdfView.tsx.
  useEffect(() => {
    if (!navOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false)
    }
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [navOpen])

  // Stable identities: both are dependencies of effects in the children that
  // consume them, and a fresh function each render would re-fire those effects.
  const clearIssueForm = useCallback(() => setOpenIssueForm(false), [])
  const clearTaskPrefill = useCallback(() => setTaskPrefill(null), [])
  const clearIssuePrefill = useCallback(() => setIssuePrefill(null), [])

  /**
   * "Create an issue" on a headline — the EOS rule that anything needing
   * discussion drops down to the issues list. Seeds the description with the
   * headline's own words and sends them to the Issues view, where the one
   * report form lives.
   */
  const makeIssueFromHeadline = useCallback((headline: Headline) => {
    setIssuePrefill({ headlineId: headline.id, taskId: null, description: headline.body })
    setView('issues')
  }, [])

  /**
   * "Create an issue" on a to-do — the Ninety "linked issue". Opens the one
   * report form with the to-do's words and the link carried through. The
   * prefill is built by issuePrefillFromTask, shared with Meeting mode.
   */
  const createIssueFromTask = useCallback((task: TaskRef) => {
    setIssuePrefill(issuePrefillFromTask(task))
    setView('issues')
  }, [])

  /**
   * "Create a task" on a headline — the other half of "a headline can become
   * an issue or a to-do". Opens the one task form; saving marks the headline
   * shared, as when one becomes an issue.
   */
  const createTaskFromHeadline = useCallback((headline: Headline) => {
    setTaskPrefill(taskPrefillFromHeadline(headline))
    setView('tasks')
  }, [])

  /**
   * "Create a task" on an issue — sends the admin to the Tasks view where the
   * one task form lives. The prefill itself is built by taskPrefillFromIssue,
   * shared with Meeting mode so the two handoffs cannot drift.
   */
  const makeTaskFromIssue = useCallback((issue: Issue) => {
    setTaskPrefill(taskPrefillFromIssue(issue))
    setView('tasks')
  }, [])

  const mark = (id: string, on: boolean) =>
    setBusy((prev) => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })

  const handleStatus = async (assignmentId: string, status: Status) => {
    if (!token) return
    const before = tasks
    // Optimistic: the tap should feel instant on a phone with poor signal.
    setTasks((prev) =>
      prev.map((t) =>
        t.assignment_id === assignmentId
          ? {
              ...t,
              status,
              completed_at:
                status === 'Completed' ? new Date().toISOString() : null,
            }
          : t,
      ),
    )
    mark(assignmentId, true)
    try {
      await setMyStatus(token, assignmentId, status)
      setProblem(null)
      // Reconcile against the server, which owns completed_at and the ordering.
      const rows = await getMyTasks(token)
      setTasks(rows)
    } catch (err) {
      setTasks(before) // roll back
      if (err instanceof InvalidLinkError) setPhase('bad-link')
      else setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      mark(assignmentId, false)
    }
  }

  const handleNote = async (assignmentId: string, note: string) => {
    if (!token) return
    const before = tasks
    const trimmed = note.trim()
    setTasks((prev) =>
      prev.map((t) =>
        t.assignment_id === assignmentId
          ? { ...t, note: trimmed === '' ? null : trimmed }
          : t,
      ),
    )
    mark(assignmentId, true)
    try {
      await setMyNote(token, assignmentId, note)
      setProblem(null)
    } catch (err) {
      setTasks(before)
      if (err instanceof InvalidLinkError) setPhase('bad-link')
      else setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      mark(assignmentId, false)
    }
  }

  if (phase === 'loading') {
    return (
      <main className="shell shell--center">
        <p className="muted">Loading your tasks…</p>
      </main>
    )
  }

  if (phase === 'no-link') {
    return (
      <SignIn
        onSignedIn={(t) => {
          storeToken(t)
          setPhase('loading')
          setToken(t)
        }}
      />
    )
  }

  if (phase === 'bad-link') {
    return (
      <main className="shell shell--center">
        <div className="panel">
          <h1 className="panel__title">Link not recognised</h1>
          <p className="muted">
            This link may have been replaced. Pick your name instead, or ask Scott for a
            new link.
          </p>
          <button
            type="button"
            className="btn"
            onClick={() => {
              forgetToken()
              setToken(null)
              setPhase('no-link')
            }}
          >
            Pick my name
          </button>
        </div>
      </main>
    )
  }

  if (phase === 'error') {
    return (
      <main className="shell shell--center">
        <div className="panel">
          <h1 className="panel__title">Could not load your tasks</h1>
          <p className="muted">{problem}</p>
          <button type="button" className="btn" onClick={() => void load(true)}>
            Try again
          </button>
        </div>
      </main>
    )
  }

  // Unreachable in practice: `phase` only becomes 'ready' after getMe(token)
  // succeeded, and load() short-circuits to 'no-link' when there is no token.
  // It exists so the render below can pass `token` to children that require a
  // string, rather than scattering non-null assertions through the JSX.
  if (!token) return null

  const buckets = bucketTasks(tasks)
  const firstName = me?.full_name.split(' ')[0] ?? 'there'

  // Tasks, Issues and Scorecard are for everyone — the floor team follows the
  // scorecard during meetings and reports the issues. The Users tab is
  // admin-only, and the database refuses those calls regardless: the tab is
  // convenience, not control. Tasks covers both your own work and everyone's,
  // in one list.
  //
  // One list, rendered once as the sidebar below — what changes with screen
  // width is how it's reached (always-visible column vs. an off-canvas
  // drawer opened by the hamburger), never `view`/`setView` or what each item
  // renders. See the "App shell / sidebar navigation" comment in styles.css.
  const navItems: { key: View; label: string; icon: IconName }[] = [
    { key: 'portal', label: 'My Portal', icon: 'portal' },
    { key: 'tasks', label: 'Tasks', icon: 'tasks' },
    { key: 'headlines', label: 'Headlines', icon: 'headlines' },
    { key: 'issues', label: 'Issues', icon: 'issues' },
    { key: 'scorecard', label: 'Scorecard', icon: 'scorecard' },
    { key: 'meeting', label: 'Meeting', icon: 'meeting' },
  ]
  if (me?.is_admin) navItems.push({ key: 'people', label: 'Users', icon: 'users' })

  // My Portal is home, so its header stays the personal greeting. Every other
  // page is somewhere you navigated to on purpose, so the header names it —
  // the same label as its entry in the sidebar/drawer, so the two always
  // agree with no separate list to keep in sync.
  const pageTitle =
    view === 'portal'
      ? `Hi ${firstName}`
      : navItems.find((item) => item.key === view)?.label ?? 'Shop Tasks'

  const goTo = (key: View) => {
    setView(key)
    // No-op on desktop, where the drawer never opens in the first place.
    setNavOpen(false)
  }

  return (
    <HeaderSlotProvider value={actionsSlot}>
    <div className="app-shell">
      {navOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <nav className={'sidebar' + (navOpen ? ' sidebar--open' : '')} aria-label="View">
        <div className="sidebar__head">
          <span className="sidebar__brand">
            <span className="sidebar__mark" aria-hidden="true">
              <Icon name="check" size={14} />
            </span>
            Shop Tasks
          </span>
          <button
            type="button"
            className="sidebar__close"
            aria-label="Close menu"
            onClick={() => setNavOpen(false)}
          >
            ✕
          </button>
        </div>
        <div className="sidebar__nav">
          {navItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={
                'sidebar__link' + (view === item.key ? ' sidebar__link--on' : '')
              }
              aria-pressed={view === item.key}
              onClick={() => goTo(item.key)}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
              {item.key === 'meeting' && meetingLive && (
                <span className="sidebar__live">Live</span>
              )}
            </button>
          ))}
        </div>
      </nav>

      <div className="app-shell__main">
        <main className="shell">
          <header className="top">
            <div className="top__start">
              <button
                type="button"
                className="menu-btn"
                aria-label="Open menu"
                aria-expanded={navOpen}
                onClick={() => setNavOpen(true)}
              >
                <span className="menu-btn__bar" />
                <span className="menu-btn__bar" />
                <span className="menu-btn__bar" />
              </button>
              <h1 className="top__hi">{pageTitle}</h1>
            </div>
            {/* Each view portals its primary action in here (HeaderAction.tsx).
                Sits between the title and Refresh on desktop; drops to its own
                row under the title on a phone, and takes no space when empty. */}
            <div className="top__slot" ref={setActionsSlot} />
            <button
              type="button"
              className="btn btn--quiet btn--refresh"
              disabled={refreshing}
              onClick={() => void load(false)}
            >
              <Icon name="refresh" size={16} className={refreshing ? 'spin' : undefined} />
              Refresh
            </button>
          </header>

          {problem && <p className="banner">{problem}</p>}

          {view === 'portal' && token ? (
            <MyPortal
              token={token}
              tasks={tasks}
              busy={busy}
              onStatus={(id, s) => void handleStatus(id, s)}
              onNote={(id, n) => void handleNote(id, n)}
              onTasksChanged={() => void load(false)}
              onOpenTab={setView}
              isAdmin={me?.is_admin ?? false}
              onCreateIssue={createIssueFromTask}
            />
          ) : view === 'headlines' && token ? (
            <Headlines
              token={token}
              isAdmin={me?.is_admin ?? false}
              onMakeIssue={makeIssueFromHeadline}
              onCreateTask={createTaskFromHeadline}
              compact
            />
          ) : view === 'issues' && token ? (
            <Issues
              token={token}
              isAdmin={me?.is_admin ?? false}
              startNew={openIssueForm}
              onStartNewHandled={clearIssueForm}
              prefill={issuePrefill}
              onPrefillUsed={clearIssuePrefill}
              onMakeTask={makeTaskFromIssue}
              compact
            />
          ) : view === 'scorecard' && token ? (
            <Scorecard token={token} isAdmin={me?.is_admin ?? false} />
          ) : view === 'meeting' && token ? (
            // Before the admin fallback below, or staff would land on the task board.
            <Meeting
              token={token}
              isAdmin={me?.is_admin ?? false}
              meId={me?.employee_id ?? null}
              tasks={tasks}
              busy={busy}
              onStatus={(id, s) => void handleStatus(id, s)}
              onNote={(id, n) => void handleNote(id, n)}
              onTasksChanged={() => void load(false)}
            />
          ) : me?.is_admin && token ? (
            <AdminView
              token={token}
              meId={me.employee_id}
              pane={view === 'people' ? 'people' : 'tasks'}
              prefill={taskPrefill}
              onPrefillUsed={clearTaskPrefill}
              onCreateIssue={createIssueFromTask}
              compact
            />
          ) : (
            <>
              {tasks.length === 0 && (
                <div className="panel">
                  <h2 className="panel__title">No tasks assigned</h2>
                  <p className="muted">When Scott assigns you something it will show up here.</p>
                </div>
              )}

              {buckets.map((bucket) => (
                <section key={bucket.key} className="group">
                  <h2 className={`group__label group__label--${bucket.key}`}>
                    {bucket.key === 'later' ? 'Tasks' : bucket.label}
                    <span className="group__count">{bucket.tasks.length}</span>
                  </h2>
                  {bucket.tasks.map((task) => (
                    <TaskCard
                      key={task.assignment_id}
                      task={task}
                      token={token}
                      busy={busy.has(task.assignment_id)}
                      onStatus={(id, s) => void handleStatus(id, s)}
                      onNote={(id, n) => void handleNote(id, n)}
                      onLinksChanged={() => void load(false)}
                      canEditDetails={me?.is_admin ?? false}
                      onCreateIssue={createIssueFromTask}
                    />
                  ))}
                </section>
              ))}
            </>
          )}

          {/*
            Identity + sign-out. This matters most on a shared computer: the token
            lives in this browser, so without a way to clear it the next person to
            open the app would land in whoever used it last, and could complete
            their tasks as them. Naming the person and offering one tap to leave is
            what stops that.
          */}
          <footer className="foot">
            <span className="muted">
              Signed in as <strong>{me?.full_name}</strong>
            </span>
            <button
              type="button"
              className="foot__out"
              onClick={() => {
                forgetToken()
                setMe(null)
                setTasks([])
                setToken(null)
                setPhase('no-link')
              }}
            >
              Not you? Sign out
            </button>
            <span className="muted foot__brand">Allied Automotive · Shop Tasks</span>
          </footer>
        </main>
      </div>
    </div>
    </HeaderSlotProvider>
  )
}
